import { gql } from '@apollo/client';
import { forPaginated } from './common/paginated.graphql';
import { ProductTransactionFragments } from './productTransaction.graphql';
import { ProductTransaction, ProductTransactionSpecifiers } from '../types/productTransaction';
import { AddressFragments } from './common/address.graphql';
import { Order } from '../types/order/';
import { CreateOrderInput, DeleteOrderInput, UpdateOrderInput } from '../types/order/order.input';

const order = gql`
  fragment Order on Order {
    id
    version
    companyId
    createdAt
    createdBy
    updatedBy
    updatedAt
    status
    type
    parentType
    parentId
    fromStockLocationId
    toStockLocationId
    contactId
    address {
      ...Address
    }
    number
    type
    assignedTo
    customFields {
      id
      name
      type
      value
      mandatory
      generated
      index
    }
    products {
      pmdId
      toStockLocationId
    }
    externalIdentifier {
      externalId
      settingsId
      type
      source
    }
  }
  ${AddressFragments.address}
`;

const get = gql`
  query Orders($page: Float, $since: Date) {
    orders(page: $page, since: $since) {
      data {
        ...Order
      }
      page
      pageSize
      batchSize
      hasNext
    }
  }
  ${order}
`;

export interface GetOrdersVariables {
  page?: number;
  since?: Date;
}

export interface GetOrdersResponse {
  orders: PaginatedOrders;
}

class PaginatedOrders extends forPaginated<Order>() {}

const create = gql`
  mutation CreateOrder($orders: [CreateOrderInput!]!) {
    createOrder(orders: $orders) {
      ...Order
    }
  }
  ${order}
`;

export interface CreateOrderVariables {
  orders: CreateOrderInput[];
}

export interface CreateOrderResponse {
  createOrder: Order[];
}

const update = gql`
  mutation UpdateOrder($orders: [UpdateOrderInput!]!) {
    updateOrder(orders: $orders) {
      ...Order
    }
  }
  ${order}
`;

export interface UpdateOrderVariables {
  orders: UpdateOrderInput[];
}

export interface UpdateOrderResponse {
  updateOrder: Order[];
}

const remove = gql`
  mutation DeleteOrder($orders: [DeleteOrderInput!]!) {
    deleteOrder(orders: $orders) {
      ...Order
    }
  }
  ${order}
`;

export interface DeleteOrderVariables {
  orders: DeleteOrderInput[];
}

export interface DeleteOrderResponse {
  deleteOrder: Order[];
}

const addOrderProduct = gql`
  mutation AddOrderProducts($orderId: String!, $specifiers: [CreateProductTransactionSpecifiersInput!]!) {
    addOrderProducts(orderId: $orderId, specifiers: $specifiers) {
      ...ProductTransaction
    }
  }
  ${ProductTransactionFragments.productTransaction}
`;

export interface AddOrderProductVariables {
  orderId: string;
  specifiers: ProductTransactionSpecifiers[];
}

export interface AddOrderProductResponse {
  addOrderProduct: ProductTransaction[];
}

const cancelOrder = gql`
  mutation CancelOrder($orderId: String!) {
    cancelOrder(orderId: $orderId) {
      ...Order
    }
  }
  ${order}
`;

export interface CancelOrderVariables {
  orderId: string;
}

export interface CancelOrderResponse {
  cancelOrder: Order;
}

const completeOrder = gql`
  mutation CompleteOrder($orderId: String!) {
    completeOrder(orderId: $orderId) {
      ...Order
    }
  }
  ${order}
`;

export interface CompleteOrderVariables {
  orderId: string;
}

export interface CompleteOrderResponse {
  completeOrder: Order;
}

export const OrderQueries = {
  get,
};

export const OrderMutations = {
  create,
  update,
  remove,
  addOrderProduct,
  cancelOrder,
  completeOrder,
};

export const OrderFragments = {
  order,
};
